/**
 * Round-trip helpers for the post-login "return to" path.
 *
 * Authentication redirects the browser away to Keycloak and back, so the page the
 * user started on has to survive the trip. It travels in the OIDC `state`
 * parameter (round-tripped untouched by Keycloak) and is restored by the callback
 * route. Because that value crosses an external redirect it is treated as
 * untrusted on the way back and validated against open redirects.
 */

/**
 * Fixed path Keycloak redirects back to after authentication.
 *
 * A single dedicated route - rather than "wherever the user happened to be" -
 * lets the realm whitelist one exact redirect URI instead of a wildcard,
 * shrinking the OIDC attack surface.
 */
export const AUTH_CALLBACK_PATH = "/auth/callback";

/** Application state round-tripped through the OIDC `state` across the redirect. */
export interface SigninState {
  /** Internal path to send the user back to once authenticated. */
  readonly returnTo: string;
}

/** Path used whenever no safe return target is available. */
const DEFAULT_RETURN_TO = "/";

/**
 * Captures where a sign-in should return to: the user's current in-app location.
 *
 * Invoked when a redirect to Keycloak is triggered (an anonymous visit or a 401
 * re-authentication). The callback path itself is never returned, otherwise a
 * completed login would bounce back to the callback instead of a real page.
 *
 * @param location Source of the current path; defaults to the live `window.location`.
 */
export function currentReturnTo(
  location: Pick<Location, "pathname" | "search"> = window.location,
): string {
  const candidate = `${location.pathname}${location.search}`;
  return isSafeReturnTo(candidate) ? candidate : DEFAULT_RETURN_TO;
}

/**
 * Extracts a safe return path from the OIDC `state` handed back by Keycloak.
 *
 * The state is untrusted (it survives a full external redirect), so only a
 * same-origin absolute path is accepted. Anything else falls back to the app root.
 */
export function resolveReturnTo(state: unknown): string {
  const candidate = readReturnTo(state);
  return candidate !== undefined && isSafeReturnTo(candidate)
    ? candidate
    : DEFAULT_RETURN_TO;
}

/** Reads the `returnTo` string off the OIDC state, if present and a string. */
function readReturnTo(state: unknown): string | undefined {
  if (typeof state === "object" && state !== null) {
    const value = (state as { returnTo?: unknown }).returnTo;
    if (typeof value === "string") {
      return value;
    }
  }
  return undefined;
}

/**
 * Accepts only same-origin absolute paths.
 *
 * Rejects the callback route (restoring it would loop) and the protocol-relative
 * (`//host`) and backslash (`/\host`) forms that browsers can resolve to a foreign
 * origin - the classic open-redirect vectors.
 */
function isSafeReturnTo(path: string): boolean {
  if (
    !path.startsWith("/") ||
    path.startsWith("//") ||
    path.startsWith("/\\")
  ) {
    return false;
  }

  const separatorIndex = path.search(/[?#]/);
  const pathname = separatorIndex === -1 ? path : path.slice(0, separatorIndex);
  return pathname !== AUTH_CALLBACK_PATH;
}
